import React, { Component } from 'react';
import { Step, Icon } from 'semantic-ui-react';

import { Filter } from 'Containers/';

export const withSteps = (steps) => {
  //verify that each step has a static stepInfo set
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];

    if (!step.stepInfo) {
      throw new Error(
        `Trying to use component ${step.displayName} as a step, but component does not have static stepInfo.`
      );
    }
  }

  //curry
  return (container) => {
    return class extends Component {
      state = {
        currentStepNumber: 0,

        stepInfo: steps.map(() => ({
          completed: false,
        })),
      };

      /** Go to the given step with respect to whether all steps prior to the
       * desired step have been completed */
      goToStep = (stepNumber) => {
        const { currentStepNumber, stepInfo } = this.state;

        if (
          currentStepNumber !== stepNumber &&
          stepInfo[stepNumber] &&
          this.getStepCompleted(stepNumber - 1)
        ) {
          this.setState({ currentStepNumber: stepNumber });
        }
      };

      /**
       * Called from the step components to see whether they're completed
       * @param {number} stepNumber - The desired step number
       * @param {bool} [thisStepOnly=false] - If false, this method will
       *   recursively check whether the given step is completed and whether all
       *   previous steps are completed
       */
      getStepCompleted = (stepNumber, thisStepOnly = false) => {
        const { currentStepNumber, stepInfo } = this.state;

        let completed = stepInfo[stepNumber]
          ? stepInfo[stepNumber].completed
          : stepNumber < currentStepNumber;

        //recursively check previous steps
        if (stepNumber > 0 && !thisStepOnly) {
          completed = completed && this.getStepCompleted(stepNumber - 1, false);
        }

        return completed;
      };

      /**
       * Called from the step components to mark themselves as completed
       * Note that steps can be completed even if their previous steps aren't,
       * they just won't show as completed/be clickable
       * @param {number} stepNumber - The desired step number
       * @param {bool} [completed=true] - Whether to mark this step as completed
       *   or incompleted
       * @param {bool} [resetNextSteps=false] - Whether to reset (mark
       *   incomplete) all the steps after the given one
       */
      setStepCompleted = (
        stepNumber,
        completed = true,
        resetNextSteps = false
      ) => {
        const stepInfo = [...this.state.stepInfo];

        stepInfo[stepNumber] = {
          ...stepInfo[stepNumber],
          completed,
        };

        if (resetNextSteps) {
          for (let i = stepNumber; i < stepInfo.length; i++) {
            stepInfo[i] = {
              ...stepInfo[i],
              completed: false,
            };
          }
        }

        this.setState({ stepInfo });
      };

      render() {
        const title = container.title || '';
        const { currentStepNumber } = this.state;

        return (
          <React.Fragment>
            <Filter title={title} newRow>
              <Step.Group widths={steps.length}>
                {steps.map(({ stepInfo }, stepNumber) => (
                  <Step
                    key={stepNumber}
                    active={stepNumber === currentStepNumber}
                    completed={this.getStepCompleted(stepNumber)}
                    disabled={!this.getStepCompleted(stepNumber - 1)}
                    onClick={() => this.goToStep(stepNumber)}
                  >
                    <Icon name={stepInfo.icon} />

                    <Step.Content>
                      <Step.Title>{stepInfo.title}</Step.Title>
                      <Step.Description>
                        {stepInfo.description}
                      </Step.Description>
                    </Step.Content>
                  </Step>
                ))}
              </Step.Group>
            </Filter>

            {steps.map((StepComponent, stepNumber) => {
              const active = stepNumber === currentStepNumber;

              return (
                //note that using display: none avoids the issue of steps being
                //re-mounted without having to check active in StepComponent's
                //render method, but it is less optimal because StepComponent's
                //children will always be rendered, just invisible
                <div
                  key={stepNumber}
                  style={{ display: active ? 'block' : 'none' }}
                >
                  <StepComponent
                    stepActive={active}
                    stepCompleted={this.getStepCompleted(stepNumber, true)}
                    goToPreviousStep={() => this.goToStep(stepNumber - 1)}
                    goToNextStep={() => this.goToStep(stepNumber + 1)}
                    setStepCompleted={(completed, resetNextSteps) =>
                      this.setStepCompleted(
                        stepNumber,
                        completed,
                        resetNextSteps
                      )
                    }
                    {...this.props}
                  />
                </div>
              );
            })}
          </React.Fragment>
        );
      }
    };
  };
};



// WEBPACK FOOTER //
// ./src/common/StepComponent.js